<template>
  <div class="jSingleFileUploader">
    <!--Image preview card-->
    <div>
      <b-card
        :class="determineThumbnail"
        :img-src="thumbnail || fileUrl"
        class="cursor-pointer"
        overlay
        @click="clickedPreviewCard">
        <i
          v-if="(thumbnail || fileUrl) && !disabled"
          class="fa fa-close fa__delete-icon"
          @click="deleteFile"/>
      </b-card>
    </div>

    <!--Image chooser form-->
    <b-form-file
      ref="fileinput"
      v-model="file"
      plain />
    <div
      class="w-100 text-center cursor-pointer"
      @click="clickBrowseButton">
      <img :src="require('@/assets/icon/camera.png')" >
      <span class="font-weight-bold">
        {{ label }}
      </span>
    </div>

    <!--Image preview modal-->
    <b-modal
      ref="imageModal"
      :ok-only="true"
      :hide-header="true"
      :hide-footer="true"
      :centered="true">
      <img :src="thumbnail || fileUrl">
    </b-modal>

    <!--Error Screen-->
    <jProblemScreen
      ref="problemScreen"
      :message="error_message"/>
    <!--Success Screen-->
    <jSuccessScreen
      ref="successScreen"
      :message="'File has been successfully uploaded'"/>
    <!--Loading Screen-->
    <jLoadingScreen ref="loadingScreen"/>
  </div>
</template>

<script>
/* eslint-disable no-unused-vars,import/no-unresolved,vue/require-default-prop,global-require,no-underscore-dangle,max-len */
import axios from 'axios';
import jProblemScreen from '../notification-screen/jProblemScreen';
import jLoadingScreen from '../notification-screen/jLoadingScreen';
import jSuccessScreen from '../notification-screen/jSuccessScreen';

export default {
  name: 'JPhotoUploader',
  components: {
    jProblemScreen, jLoadingScreen, jSuccessScreen,
  },
  props: {
    label: {
      type: String,
      default: 'Upload Photo',
    },
    imgDefault: {
      type: String,
      default: null,
    },
    apiToken: {
      type: String,
      default: '',
    },
    urlUpload: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: 5000,
    },
  },
  data() {
    return {
      file: null,
      fileName: '',
      thumbnail: null,
      fileUrl: '',
      fileType: '',
      fileSize: '',
      isImageShow: false,
      error_message: '',
      acceptedFileType: ['jpg', 'png', 'jpeg'],
    };
  },
  computed: {
    determineThumbnail() {
      if (!this.file && !this.fileUrl) {
        return 'card--no-file';
      }
      return 'card-file';
    },
    checkMode() {
      if (this.fileUrl || this.file) {
        return 'update';
      }
      return 'upload';
    },
    fileExtensionsSentence() {
      return `${this.acceptedFileType.slice(0, this.acceptedFileType.length - 1).join(', ')}, and ${this.acceptedFileType.slice(-1)}`;
    },
  },
  watch: {
    imgDefault(val) {
      this.fileUrl = val;
    },
    fileUrl(val) {
      this.$emit('url', val);
    },
    file(val) {
      if (val) {
        const fileName = this.file.name;
        const fileSize = this.file.size;
        const dotPosition = fileName.lastIndexOf('.') + 1;
        let extension = fileName.substring(dotPosition);
        extension = extension.toLowerCase();
        this.fileType = extension;

        let isAccepted = false;
        this.acceptedFileType.forEach((type) => {
          if (type === extension) {
            isAccepted = true;
          }
        });

        let isSizeAccepted = false;
        if (this.maxFileSize) {
          if (fileSize < this.maxFileSize * 1000) {
            isSizeAccepted = true;
          }
        } else {
          isSizeAccepted = true;
        }

        if (isAccepted && isSizeAccepted) {
          this.uploadFile(this.file);
          this.fileName = fileName;
          this.fileSize = fileSize;
        } else {
          if (!isAccepted) {
            this.error_message = 'Please choose a wanted file!';
            this.clearFiles();
            this.$refs.problemScreen.toggleProblemScreen();
          }
          if (!isSizeAccepted) {
            this.error_message = `Please upload a file less than ${this.maxFileSize / 1000}MB!`;
            this.clearFiles();
            this.$refs.problemScreen.toggleProblemScreen();
          }
          if (!isSizeAccepted && !isAccepted) {
            this.error_message = 'Please choose a wanted file!';
            this.clearFiles();
            this.$refs.problemScreen.toggleProblemScreen();
          }
        }
      }
    },
    isImageShow() {
      if (this.isImageShow) {
        this.showImageModal();
      } else {
        this.hideImageModal();
      }
    },
  },
  created() {
    this.fileUrl = this.imgDefault;
  },
  methods: {
    clearFiles() {
      this.fileUrl = '';
      this.$refs.fileinput.reset();
    },

    deleteFile() {
      this.file = null;
      this.fileName = '';
      this.thumbnail = null;
      this.fileType = '';

      this.clearFiles();
    },

    showImageModal() {
      this.$refs.imageModal.show();
    },

    hideImageModal() {
      this.$refs.imageModal.hide();
      this.clickedPreviewCard();
    },

    clickedPreviewCard() {
      if (this.file !== null) {
        if (this.fileType === 'jpg' || this.fileType === 'png' || this.fileType === 'jpeg' || this.fileType === 'bmp') {
          this.isImageShow = !this.isImageShow;
        }
      } else if (this.fileUrl !== null) {
        const dotPosition = this.fileUrl.lastIndexOf('.') + 1;
        let extension = this.fileUrl.substring(dotPosition);
        extension = extension.toLowerCase();

        if (extension === 'jpg' || extension === 'png' || extension === 'jpeg' || extension === 'bmp') {
          this.isImageShow = !this.isImageShow;
        }
      }
    },

    clickBrowseButton() {
      this.$refs.fileinput.$refs.input.click();
    },

    uploadFailed() {
      this.file = null;
      this.fileUrl = '';
    },

    uploadSuccess(thumbnail, fileUrl) {
      this.thumbnail = thumbnail;
      this.fileUrl = fileUrl;
      this.$emit('get-data', fileUrl);
    },

    getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },

    async uploadFile(file) {
      this.startUpload();
      const apiKey = this.apiToken;
      const imagebase = await this.getBase64(file).then(
        data => data,
      );
      const commaPosition = imagebase.lastIndexOf(',') + 1;
      const imageData = imagebase.substring(commaPosition);
      const fileName = file.name;
      let sendData = {};
      sendData = {
        filename: fileName,
        content: imageData,
      };

      axios.post(this.urlUpload, sendData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: apiKey,
        },
      }).then((res) => {
        const data = res.data.data;
        const imageUrl = data.url;
        this.uploadSuccess(imageUrl, imageUrl);
        this.finallySuccess();
      }).catch((error) => {
        this.error_message = error.response.data.message;
        this.uploadFailed();
        this.finallyFailed();
      });
    },

    startUpload() {
      this.$refs.loadingScreen.showLoadingScreen();
    },
    finallySuccess() {
      this.$refs.loadingScreen.closeLoadingScreen();
      this.$refs.successScreen.toggleSuccessScreen();
    },
    finallyFailed() {
      this.$refs.loadingScreen.closeLoadingScreen();
      this.$refs.problemScreen.toggleProblemScreen();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/upload-foto/jPhotoUploader.vue